/* This file is used to create query options for the query client. Only specify the query key, queryFn, and staleTime. For additional options that are more specific to the location of the query usage, spread the query options object and add the additional options. 
For example:
useSuspenseQuery({
    ...bookmarkedSOPsOptions(TENANT_ID!, idToken!),
    refetchInterval: REFRESH_INTERVAL,
  })
*/

import {
  DOCUMENT_STALE_TIME,
  USER_INFO_STALE_TIME,
  ID_TOKEN_STALE_TIME,
  DEFAULT_STALE_TIME,
  VIDEO_URL_STALE_TIME,
  PROCESSING_POLL_INTERVAL,
} from './constants'
import {
  getDocumentDownloadUrl,
  getReferenceDocument,
  getReferenceDocumentsByIDs,
  getSettings,
  getUserInfoById,
  getUserMe,
  type Name,
  getVideoThumbnail,
  queryKeys,
  getUserMeProfile,
  getDocumentVersionHistoryById,
  getDocumentVersionHistoryByVersion,
  getOrgPermissionsCurrentUser,
  getProjectMembers,
} from './queries'
import { queryOptions } from '@tanstack/react-query'
import {
  getDocuments,
  getBookmarkedSOPs,
  getBookmarkedDocuments,
  getAllBookmarks,
  listOrgUsers,
  getDocumentsCreatedByMe,
  getTrashDocuments,
  getDocumentsInProject,
  getDocumentDataById,
  getTenantProjectById,
  getTenantProjects,
  getReferenceDocumentsInProject,
  getOrgQuotas,
  getDocumentEmbeddingStatus,
  getDocumentClips,
  getSOPDiagnostics,
} from './queries'
import type { IdTokenResult } from 'firebase/auth'

export function allDocumentsOptions({
  tenantId,
  token,
  stage,
  offset = 0,
  limit = 50,
  enabled = true,
}: {
  tenantId: string
  token: string
  stage?: 'draft' | 'published' | 'pending_changes'
  offset?: number
  limit?: number
  enabled?: boolean
}) {
  return queryOptions({
    queryKey: [...queryKeys.documents.all(tenantId, stage), offset, limit],
    queryFn: () => getDocuments({ token, tenantId, stage, offset, limit }),
    staleTime: DOCUMENT_STALE_TIME,
    enabled,
  })
}

export function bookmarkedSOPsOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.bookmarks.sops(tenantId),
    queryFn: () => getBookmarkedSOPs({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function bookmarkedDocumentsOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.bookmarks.documents(tenantId),
    queryFn: () => getBookmarkedDocuments({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function allBookmarksOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.bookmarks.combined(tenantId),
    queryFn: () => getAllBookmarks({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function usersOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.users.all(tenantId),
    queryFn: () => listOrgUsers({ token, tenantId }),
    staleTime: USER_INFO_STALE_TIME,
  })
}

export function orgPermissionsCurrentUserOptions(
  tenantId: string,
  token: string,
) {
  return queryOptions({
    queryKey: queryKeys.orgPermissions.currentUser(tenantId),
    queryFn: () => getOrgPermissionsCurrentUser({ token, tenantId }),
    staleTime: USER_INFO_STALE_TIME,
  })
}

export function createdByMeDocumentsOptions(
  token: string,
  tenantId: string,
  userId: string,
) {
  return queryOptions({
    queryKey: queryKeys.documents.createdByMe,
    queryFn: () => getDocumentsCreatedByMe({ token, tenantId, userId }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function trashDocumentsOptions(token: string, tenantId: string) {
  return queryOptions({
    queryKey: queryKeys.documents.trash,
    queryFn: () => getTrashDocuments({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function projectDocumentsOptions({
  token,
  tenantId,
  projectId,
  stage,
  folder,
  enabled = true,
}: {
  token: string
  tenantId: string
  projectId: string
  stage?: 'draft' | 'published' | 'pending_changes'
  folder?: string
  enabled?: boolean
}) {
  return queryOptions({
    queryKey: queryKeys.documents.byProject(tenantId, projectId, stage, folder),
    queryFn: () =>
      getDocumentsInProject({ token, tenantId, projectId, stage, folder }),
    staleTime: DOCUMENT_STALE_TIME,
    enabled,
  })
}

export function documentByIdOptions(
  token: string,
  tenantId: string,
  projectId: string,
  docId: string,
) {
  return queryOptions({
    queryKey: queryKeys.documents.byId(docId),
    queryFn: () => getDocumentDataById({ token, tenantId, projectId, docId }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function referenceDocumentByIdOptions(
  token: string,
  tenantId: string,
  projectId: string,
  referenceDocumentId: string,
  staleTime: number = DEFAULT_STALE_TIME,
) {
  return queryOptions({
    queryKey: queryKeys.referenceDocuments.byId(referenceDocumentId),
    queryFn: () =>
      getReferenceDocument({ token, tenantId, projectId, referenceDocumentId }),
    staleTime,
  })
}

export function referenceDocumentsByIDsOptions(
  token: string,
  tenantId: string,
  projectId: string,
  referenceDocumentIds: string[],
  staleTime: number = DEFAULT_STALE_TIME,
) {
  return queryOptions({
    queryKey: queryKeys.referenceDocuments.byIds(referenceDocumentIds),
    queryFn: () =>
      getReferenceDocumentsByIDs({
        token,
        tenantId,
        projectId,
        referenceDocumentIds,
      }),
    staleTime,
  })
}

export function projectDetailsOptions(
  token: string,
  tenantId: string,
  projectId: string,
) {
  return queryOptions({
    queryKey: queryKeys.projects.byId(projectId),
    queryFn: () => getTenantProjectById({ token, tenantId, projectId }),
  })
}

export function bookmarkedSOPsInProjectOptions(
  token: string,
  tenantId: string,
  projectId: string,
) {
  return queryOptions({
    queryKey: queryKeys.bookmarks.sops(tenantId),
    queryFn: () => getBookmarkedSOPs({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
    select: (data) => data.filter((doc) => doc.projectId === projectId),
  })
}

export function bookmarkedDocumentsInProjectOptions(
  token: string,
  tenantId: string,
  projectId: string,
) {
  return queryOptions({
    queryKey: queryKeys.bookmarks.documents(tenantId),
    queryFn: () => getBookmarkedDocuments({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
    select: (data) => data.filter((doc) => doc.projectId === projectId),
  })
}

export function allBookmarksInProjectOptions(
  token: string,
  tenantId: string,
  projectId: string,
) {
  return queryOptions({
    queryKey: queryKeys.bookmarks.combined(tenantId),
    queryFn: () => getAllBookmarks({ token, tenantId }),
    staleTime: DOCUMENT_STALE_TIME,
    select: (data) => ({
      sops: data.sops.filter((doc) => doc.projectId === projectId),
      documents: data.documents.filter((doc) => doc.projectId === projectId),
    }),
  })
}

export function idTokenOptions(
  userId: string,
  getIdTokenFn: () => Promise<string>,
) {
  return queryOptions({
    queryKey: queryKeys.auth.idToken(userId),
    queryFn: getIdTokenFn,
    staleTime: ID_TOKEN_STALE_TIME,
    refetchInterval: ID_TOKEN_STALE_TIME,
  })
}

export function idTokenResultOptions(
  userId: string,
  getIdTokenResultFn: () => Promise<IdTokenResult>,
) {
  return queryOptions({
    queryKey: queryKeys.auth.idTokenResult(userId),
    queryFn: getIdTokenResultFn,
    staleTime: ID_TOKEN_STALE_TIME,
    refetchInterval: ID_TOKEN_STALE_TIME,
  })
}

export function imageCacheOptions(imageUrl?: string) {
  const CACHE_TIME = 1000 * 60 * 60 * 24 // 24 hours
  const STALE_TIME = 1000 * 60 * 60 // 1 hour
  return queryOptions({
    queryKey: queryKeys.images.byUrl(imageUrl ?? ''),
    queryFn: async () => {
      if (!imageUrl) return null
      const response = await fetch(imageUrl)
      if (!response.ok) throw new Error('Failed to fetch image')
      const blob = await response.blob()
      return URL.createObjectURL(blob)
    },
    enabled: !!imageUrl,
    gcTime: CACHE_TIME,
    staleTime: STALE_TIME,
    retry: 2,
  })
}

export function projectMembersOptions(
  tenantId: string,
  projectId: string,
  token: string,
) {
  return queryOptions({
    queryKey: queryKeys.projects.members(tenantId, projectId),
    queryFn: () => getProjectMembers({ token, tenantId, projectId }),
    staleTime: DEFAULT_STALE_TIME,
  })
}

export function tenantProjectsOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.projects.all(tenantId),
    queryFn: () => getTenantProjects({ token, tenantId }),
    staleTime: DEFAULT_STALE_TIME,
  })
}

export function videoThumbnailOptions(
  idToken: string,
  tenantId: string,
  projectId: string,
  source: string,
  position: number,
  resolution?: 'SD' | 'HD' | 'FHD' | 'Original',
  quality?: number,
) {
  return queryOptions({
    queryKey: ['videoThumbnail', tenantId, projectId, source, position],
    queryFn: async () =>
      [
        source,
        position,
        await getVideoThumbnail({
          token: idToken,
          tenantId,
          projectId,
          source,
          position,
          resolution,
          quality,
        }).catch((error) => {
          console.error('Error fetching video thumbnail', error)
          return null
        }),
      ] as [string, number, string],
    staleTime: VIDEO_URL_STALE_TIME,
  })
}

export function documentDownloadUrlOptions(
  idToken: string,
  tenantId: string,
  projectId: string,
  source: string,
) {
  return queryOptions({
    queryKey: ['documentDownloadUrl', tenantId, projectId, source],
    queryFn: async () =>
      [
        source,
        await getDocumentDownloadUrl({
          token: idToken,
          tenantId,
          projectId,
          source,
        }).then((res) => res.url),
      ] as [string, string],
    staleTime: DEFAULT_STALE_TIME,
  })
}

export function referenceDocumentsInProjectOptions(
  idToken: string,
  tenantId: string,
  projectId: string,
) {
  return queryOptions({
    queryKey: queryKeys.referenceDocuments.all(tenantId, projectId),
    queryFn: () =>
      getReferenceDocumentsInProject({ token: idToken, tenantId, projectId }),
  })
}

export function userMeOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.users.me(tenantId),
    queryFn: () => getUserMe({ token, tenantId }),
    staleTime: USER_INFO_STALE_TIME,
  })
}

export function userMeProfileOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.users.meProfile(tenantId),
    queryFn: () => getUserMeProfile({ token, tenantId }),
    staleTime: USER_INFO_STALE_TIME,
  })
}

export function userInfoByIdOptions(
  tenantId: string,
  token: string,
  userId: string,
  enabled: boolean = true,
) {
  return queryOptions({
    queryKey: queryKeys.users.byIdProfile(userId),
    queryFn: () => getUserInfoById({ token, tenantId, userId }),
    staleTime: USER_INFO_STALE_TIME,
    enabled,
    throwOnError: false,
  })
}

export function orgSettingsOptions<S>(
  tenantId: string,
  token: string,
  name: Name<S>,
) {
  return queryOptions({
    queryKey: queryKeys.settings.byName(name),
    queryFn: () => getSettings({ token, tenantId, name }),
    staleTime: DEFAULT_STALE_TIME,
  })
}

export function documentVersionHistoryByIdOptions(
  idToken: string,
  tenantId: string,
  projectId: string,
  documentId: string,
) {
  return queryOptions({
    queryKey: queryKeys.documents.versionHistoryById(documentId),
    queryFn: () =>
      getDocumentVersionHistoryById({
        token: idToken,
        tenantId,
        projectId,
        documentId,
      }),
  })
}

export function documentVersionHistoryByVersionOptions(
  idToken: string,
  tenantId: string,
  projectId: string,
  documentId: string,
  version: number,
) {
  return queryOptions({
    queryKey: queryKeys.documents.versionHistoryByVersion(documentId, version),
    queryFn: () =>
      getDocumentVersionHistoryByVersion({
        token: idToken,
        tenantId,
        projectId,
        documentId,
        version,
      }),
    staleTime: DOCUMENT_STALE_TIME,
  })
}

export function orgQuotasOptions(tenantId: string, token: string) {
  return queryOptions({
    queryKey: queryKeys.org.quotas(tenantId),
    queryFn: () => getOrgQuotas({ token, tenantId }),
    staleTime: DEFAULT_STALE_TIME,
  })
}

export function documentEmbeddingStatusOptions(
  token: string,
  tenantId: string,
  projectId: string,
  docId: string,
  polling: boolean = false,
) {
  return queryOptions({
    queryKey: queryKeys.documents.embeddingStatus(docId),
    queryFn: () =>
      getDocumentEmbeddingStatus({ token, tenantId, projectId, docId }),
    staleTime: 0,
    refetchInterval: polling ? PROCESSING_POLL_INTERVAL : false,
  })
}

export function documentClipsOptions({
  tenantId,
  token,
  projectId,
  documentId,
  enabled = true,
}: {
  tenantId: string
  token: string
  projectId: string
  documentId: string
  enabled?: boolean
}) {
  return queryOptions({
    queryKey: queryKeys.clips.byDocument(tenantId, projectId, documentId),
    queryFn: () => getDocumentClips({ token, tenantId, projectId, documentId }),
    staleTime: DEFAULT_STALE_TIME,
    enabled,
  })
}

export function sopDiagnosticsOptions(
  token: string,
  tenantId: string,
  projectId: string,
  sopId: string,
) {
  return queryOptions({
    queryKey: queryKeys.documents.sopDiagnostics(sopId),
    queryFn: () => getSOPDiagnostics({ token, tenantId, projectId, sopId }),
    // Diagnostics is dev-only and read-on-demand — staleTime 0 so a manual
    // tab switch always re-fetches.
    staleTime: 0,
  })
}

// Add more query/mutation options factories as you refactor other queries
